// Decile chart — mean equivalised household income (after all taxes, incl.
// VAT/excise) per decile, with a ghost outline of the baseline behind each
// reform bar, a median reference line, and the HeadlineMetric block above.
//
// The story is the *level* per decile plus the change from baseline.
// Bars sit on the absolute euro axis at the reform value; a dashed
// outline at the baseline value reads as "this is where you were".
// Bars are direction-coloured by the sign of the reform delta —
// Household income's goodWhen is "increasing", so reform > baseline
// renders green.
//
// When isModified is false, the ghost outline is suppressed (it would
// equal the filled bar) and bars render in the neutral brand colour.

import type { EChartsOption } from "echarts";
import {
  GAIN_COLOUR,
  LOSS_COLOUR,
  dimensionByKey,
  isGoodDelta,
} from "../../lib/dimensions";
import type { DecileRecord, EngineOutput } from "../../lib/types";
import { Chart } from "../chart";
import { HeadlineMetric } from "../headline_metric";

const BASELINE_COLOUR = "#5e4a97"; // brand primary
const GHOST_BORDER = "#a39bbd"; // brand primary, lighter
const MEDIAN_LINE_COLOUR = "#8a8a8d";

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

export function DecileChart({
  engineOutput,
  baselineOutput,
  isModified,
}: Props): JSX.Element {
  const reformRecords = engineOutput.results.aggregator;
  const baselineRecords =
    baselineOutput?.results.aggregator ?? reformRecords;

  // Pull the median directly from the engine's summary_stats. The
  // engine emits median (not mean) under `household_income` because
  // distributional analysis uses median; mean is pulled upward by the
  // top decile and misrepresents the typical household. Pulling
  // through summary_stats guarantees the headline metric, the
  // reference line, and the caption all show the same number by
  // construction.
  const medianBaseline =
    baselineOutput?.summary_stats.household_income ??
    engineOutput.summary_stats.household_income;
  const medianReform = engineOutput.summary_stats.household_income;

  return (
    <div className="beamm-decile-chart" data-testid="decile-chart">
      <HeadlineMetric
        dimension={dimensionByKey.household_income}
        label="Median household equivalent income post-consumption taxes"
        tooltip="Median household equivalent income post-consumption taxes is the equivalised disposable income of the household at the middle of the income distribution after accounting for taxes paid on consumption. Equivalised income adjusts household income for differences in household size and composition, allowing living standards to be compared across households. Half of individuals live in households with a lower income than this figure, and half in households with a higher income."
        baseline={medianBaseline}
        reform={medianReform}
        isModified={isModified}
      />
      <Chart
        option={buildOption(
          reformRecords,
          baselineRecords,
          medianBaseline,
          isModified,
        )}
        height="360px"
      />
      {/* Visually hidden but reachable by screen readers and Cypress.
       *  The dashed reference line on the chart canvas has no DOM-level
       *  hook (echarts renders to canvas); this span carries both the
       *  accessible description and the stable test contract. */}
      <span className="beamm-sr-only" data-testid="median-reference">
        Baseline median: €{Math.round(medianBaseline).toLocaleString("en-GB")} per month
      </span>
    </div>
  );
}

function buildOption(
  reformRecords: DecileRecord[],
  baselineRecords: DecileRecord[],
  medianBaseline: number,
  isModified: boolean,
): EChartsOption {
  const reformData = reformRecords.map((r, i) => {
    const b = baselineRecords[i];
    const delta = r.equivalised_income_mean - b.equivalised_income_mean;
    const colour =
      isModified && Math.abs(delta) > 1
        ? isGoodDelta(delta, "increasing")
          ? GAIN_COLOUR
          : LOSS_COLOUR
        : BASELINE_COLOUR;
    return { value: r.equivalised_income_mean, itemStyle: { color: colour } };
  });

  // Per-decile baseline reference: a thin horizontal tick at the
  // baseline value, rendered above the filled bar via a scatter
  // series. Just the top edge of "where baseline was" — no rectangle
  // outline noise.
  const baselineTickData = baselineRecords.map((b, i) => [
    i,
    b.equivalised_income_mean,
  ]);

  return {
    grid: { left: "12%", right: "5%", top: "12%", bottom: "15%" },
    xAxis: {
      type: "category",
      name: "Decile",
      nameLocation: "middle",
      nameGap: 28,
      data: reformRecords.map((r) => String(r.decile)),
    },
    yAxis: {
      type: "value",
      name: "€ / month",
      axisLabel: {
        formatter: (v: number) =>
          `€${Math.round(v).toLocaleString("en-GB")}`,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#ebe7f1" } },
    },
    legend: { show: false },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: (rawParams: unknown) => {
        const params = rawParams as Array<{ axisValue: string }>;
        const decile = params[0].axisValue;
        const idx = Number(decile) - 1;
        const r = reformRecords[idx];
        const b = baselineRecords[idx];
        const delta = r.equivalised_income_mean - b.equivalised_income_mean;
        const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
        const formatEur = (v: number) =>
          `€${Math.round(v).toLocaleString("en-GB")}`;
        const deltaText = isModified
          ? `${sign}${formatEur(Math.abs(delta))}`
          : "baseline";
        return [
          `<strong>Decile ${decile}</strong>`,
          `baseline: ${formatEur(b.equivalised_income_mean)}`,
          `reform: ${formatEur(r.equivalised_income_mean)}`,
          `change: ${deltaText}`,
        ].join("<br/>");
      },
    },
    series: [
      // Filled reform bars. Rendered first so the ghost outline above
      // sits on top of the filled body.
      {
        type: "bar",
        data: reformData,
        barWidth: "60%",
        z: 1,
        markLine: {
          silent: true,
          symbol: "none",
          // Inline label removed — the caption beneath the chart
          // carries the median value, and the on-chart label was
          // colliding with bar 7's body. Line itself stays as the
          // visual reference.
          label: { show: false },
          data: [
            {
              yAxis: medianBaseline,
              lineStyle: {
                color: MEDIAN_LINE_COLOUR,
                width: 1,
                type: "dashed",
              },
            },
          ],
        },
      },
      // Per-bar baseline tick mark. Only when modified — in unmodified
      // state baseline == reform so the tick would just sit on top of
      // the filled bar's top edge. Rendered as a scatter series with a
      // rectangular symbol stretched horizontally to bar width and made
      // thin vertically, so each decile gets a clear "baseline was
      // here" reference without the noise of a full outline rectangle.
      ...(isModified
        ? [
            {
              type: "scatter" as const,
              data: baselineTickData,
              symbol: "rect",
              symbolSize: [42, 3],
              itemStyle: {
                color: GHOST_BORDER,
              },
              z: 3,
              silent: true,
              tooltip: { show: false },
            },
          ]
        : []),
    ],
  };
}

